/**
 * Partner settlement (ortaklar arası hesaplaşma). `contributed` is each partner's
 * net contribution from v_partner_positions (expenses paid personally + money put
 * into the company − reimbursements). With ownership shares, a partner's fair
 * share of the total is total × share%. The difference tells who owes whom.
 */
import { D, MoneyDecimal, round2, sum, type Dec, type DecimalInput } from './money';

export interface PartnerPosition {
  id: string;
  name: string;
  contributed: DecimalInput;
  /** ownership share in %, null = split equally among partners without a share */
  sharePct?: DecimalInput | null;
}

export interface PartnerSettlementRow {
  id: string;
  name: string;
  contributed: Dec;
  sharePct: Dec;
  fairShare: Dec;
  /** + over-contributed (others owe this partner), − under-contributed */
  difference: Dec;
}

export interface PartnerTransfer {
  fromId: string;
  toId: string;
  amount: Dec;
}

export interface PartnerSettlement {
  total: Dec;
  rows: PartnerSettlementRow[];
  transfers: PartnerTransfer[];
  sharesValid: boolean;
}

export function resolveShares(partners: PartnerPosition[]): Dec[] {
  const explicit = partners.map((p) => (p.sharePct === null || p.sharePct === undefined || p.sharePct === '' ? null : D(p.sharePct)));
  const assigned = sum(explicit.filter((x): x is Dec => x !== null));
  const missing = explicit.filter((x) => x === null).length;
  const rest = missing ? new MoneyDecimal(100).minus(assigned).dividedBy(missing) : D(0);
  return explicit.map((x) => x ?? (rest.isNegative() ? D(0) : rest));
}

export function settlePartners(partners: PartnerPosition[]): PartnerSettlement {
  const shares = resolveShares(partners);
  const total = sum(partners.map((p) => p.contributed));
  const rows: PartnerSettlementRow[] = partners.map((p, i) => {
    const share = shares[i]!;
    const fairShare = round2(total.times(share).dividedBy(100));
    const contributed = D(p.contributed);
    return { id: p.id, name: p.name, contributed, sharePct: share, fairShare, difference: round2(contributed.minus(fairShare)) };
  });

  // greedy matching: under-contributors pay over-contributors
  const creditors = rows.filter((r) => r.difference.greaterThan(0)).map((r) => ({ id: r.id, left: r.difference }));
  const debtors = rows.filter((r) => r.difference.lessThan(0)).map((r) => ({ id: r.id, left: r.difference.abs() }));
  const transfers: PartnerTransfer[] = [];
  let ci = 0;
  let di = 0;
  while (ci < creditors.length && di < debtors.length) {
    const c = creditors[ci]!;
    const d = debtors[di]!;
    const amount = MoneyDecimal.min(c.left, d.left);
    if (amount.greaterThanOrEqualTo('0.01')) transfers.push({ fromId: d.id, toId: c.id, amount: round2(amount) });
    c.left = c.left.minus(amount);
    d.left = d.left.minus(amount);
    if (c.left.lessThan('0.01')) ci++;
    if (d.left.lessThan('0.01')) di++;
  }
  const sharesValid = sum(shares).minus(100).abs().lessThan('0.0001');
  return { total, rows, transfers, sharesValid };
}

export interface PartnerCurrencyLine {
  currency: string;
  /** in `currency` */
  amount: Dec;
  /** TRY (base) at the rates frozen on each transaction */
  amountBase: Dec;
}

export interface PartnerSummary {
  id: string;
  name: string;
  userId: string | null;
  sharePct: DecimalInput | null;
  totalBase: Dec;
  /** non-zero lines, base currency first */
  lines: PartnerCurrencyLine[];
}

/**
 * v_partner_positions has one row per partner and currency. Group them per partner:
 * the TRY-equivalent total drives the settlement, the currency lines keep the detail
 * (e.g. 250 $ paid for samples stays 250 $).
 */
export function groupPartnerPositions(
  rows: { contact_id: string | null; name: string | null; user_id: string | null; share_pct: DecimalInput | null; currency: string | null; contributed: DecimalInput | null; contributed_base: DecimalInput | null }[],
  base: string,
): PartnerSummary[] {
  const byId = new Map<string, PartnerSummary>();
  for (const r of rows) {
    if (!r.contact_id) continue;
    let p = byId.get(r.contact_id);
    if (!p) {
      p = { id: r.contact_id, name: r.name ?? '', userId: r.user_id, sharePct: r.share_pct, totalBase: D(0), lines: [] };
      byId.set(r.contact_id, p);
    }
    if (!r.currency) continue;
    const amount = D(r.contributed ?? 0);
    // base-currency rows carry no separate base value when they come from invoices only
    const amountBase = r.currency === base ? amount : D(r.contributed_base ?? 0);
    // a currency line with nothing left in its own currency has nothing left to settle
    if (amount.isZero()) continue;
    p.lines.push({ currency: r.currency, amount, amountBase });
    p.totalBase = p.totalBase.plus(amountBase);
  }
  for (const p of byId.values()) {
    p.lines.sort((a, b) => (a.currency === base ? -1 : b.currency === base ? 1 : a.currency.localeCompare(b.currency)));
    p.totalBase = round2(p.totalBase);
  }
  return [...byId.values()];
}

export interface PartnerExpenseTx {
  account_id: string;
  type: string;
  currency: string;
  amount: DecimalInput;
  amount_base: DecimalInput;
  category_id?: string | null;
  category?: { name: string | null } | null;
}

export interface PartnerExpenseCategory {
  categoryId: string | null;
  name: string;
  totalBase: Dec;
  /** TRY equivalent per partner (contact id) */
  byPartner: { partnerId: string; base: Dec }[];
  /** original amounts per currency */
  byCurrency: { currency: string; amount: Dec }[];
  count: number;
}

/**
 * What partners paid out of pocket, per category: expenses/fees posted on partner
 * accounts. `partnerOf` maps a partner account id to its partner (contact) id.
 * Sorted by TRY equivalent, largest first.
 */
export function partnerExpenseBreakdown(txs: PartnerExpenseTx[], partnerOf: Map<string, string>, uncategorised: string): PartnerExpenseCategory[] {
  const byCat = new Map<string, PartnerExpenseCategory>();
  for (const t of txs) {
    const partnerId = partnerOf.get(t.account_id);
    if (!partnerId || (t.type !== 'expense' && t.type !== 'fee')) continue;
    const key = t.category_id ?? '∅';
    let c = byCat.get(key);
    if (!c) {
      c = { categoryId: t.category_id ?? null, name: t.category?.name ?? uncategorised, totalBase: D(0), byPartner: [], byCurrency: [], count: 0 };
      byCat.set(key, c);
    }
    const base = D(t.amount_base);
    c.totalBase = c.totalBase.plus(base);
    c.count++;
    const p = c.byPartner.find((x) => x.partnerId === partnerId);
    if (p) p.base = p.base.plus(base);
    else c.byPartner.push({ partnerId, base });
    const cur = c.byCurrency.find((x) => x.currency === t.currency);
    if (cur) cur.amount = cur.amount.plus(D(t.amount));
    else c.byCurrency.push({ currency: t.currency, amount: D(t.amount) });
  }
  return [...byCat.values()]
    .map((c) => ({ ...c, totalBase: round2(c.totalBase) }))
    .sort((a, b) => b.totalBase.comparedTo(a.totalBase) || a.name.localeCompare(b.name, 'tr'));
}
